import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, Flag, Reply as ReplyIcon, Trash2, CornerDownRight, X } from "lucide-react";
import { ForumAuthorBadges } from "@/components/forum/ForumAuthorBadges";
import { Linkify } from "@/components/forum/Linkify";
import { ForumMediaUploader } from "@/components/forum/ForumMediaUploader";
import { MentionTextarea } from "@/components/forum/MentionTextarea";

export const FORUM_REPORT_REASONS = [
  { value: "racism_hate", label: "Racism / hate" },
  { value: "sales_post", label: "Sales post (belongs in Marketplace)" },
  { value: "spam", label: "Spam or scam" },
  { value: "harassment", label: "Harassment" },
  { value: "off_topic", label: "Off-topic / wrong category" },
  { value: "other", label: "Other" },
];

function joinedLabel(iso) {
  if (!iso) return null;
  try { return `Joined ${new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" })}`; }
  catch { return null; }
}
function locationLabel(city, state, country) {
  if (city && state) return `${city}, ${state}`;
  if (city && country) return `${city}, ${country}`;
  return city || state || country || null;
}

function Avatar({ src, username, P, size = 48 }) {
  const initials = (username || "?").slice(0, 2).toUpperCase();
  if (src) return <img src={src} alt="" style={{ width: size, height: size, borderColor: P.cardBorder }} className="object-cover border shrink-0" />;
  return (
    <div style={{ width: size, height: size, background: P.rowSurface, border: `1px solid ${P.cardBorder}`, color: P.lime }}
      className="shrink-0 flex items-center justify-center font-heading text-sm">{initials}</div>
  );
}

// The fixed-width left author panel. Banner strip + avatar when the member set a banner, else
// avatar-only. Under the name: role/ambassador/founding badges, join date, and location.
function AuthorPanel({ post, P }) {
  const loc = locationLabel(post.author_city, post.author_state, post.author_country);
  const joined = joinedLabel(post.author_joined_at);
  return (
    <div className="sm:w-[156px] sm:shrink-0 sm:border-r sm:pr-3 flex sm:flex-col items-center sm:items-start gap-3 sm:gap-0"
      style={{ borderColor: P.cardBorder }} data-testid="forum-author-panel">
      <div className="sm:w-full sm:mb-2">
        {post.author_banner_url ? (
          <div className="relative sm:mb-1">
            <div className="hidden sm:block h-11 w-full bg-cover bg-center border" data-testid="forum-author-banner"
              style={{ backgroundImage: `url(${post.author_banner_url})`, borderColor: P.cardBorder }} />
            <div className="sm:-mt-6 sm:pl-1"><Avatar src={post.author_avatar} username={post.author_username} P={P} /></div>
          </div>
        ) : (
          <Avatar src={post.author_avatar} username={post.author_username} P={P} />
        )}
      </div>
      <div className="min-w-0 sm:w-full">
        <p className="font-mono text-[13px] font-bold truncate" style={{ color: P.lime }} data-testid="forum-author-name">@{post.author_username}</p>
        <div className="mt-1.5"><ForumAuthorBadges role={post.author_role} badges={post.author_badges} accountType={post.author_account_type} /></div>
        {joined && <p className="mt-1.5 font-mono text-[10px]" style={{ color: P.textMuted }} data-testid="forum-author-joined">{joined}</p>}
        {loc && <p className="font-mono text-[10px]" style={{ color: P.textMuted }} data-testid="forum-author-location">{loc}</p>}
      </div>
    </div>
  );
}

function ReactBtn({ icon: Icon, count, active, activeColor, onClick, testid, title, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
      className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide px-2.5 py-1.5 border transition-colors disabled:opacity-50"
      style={active
        ? { color: "#fff", background: activeColor, borderColor: activeColor }
        : { color: "inherit", borderColor: "currentColor", opacity: 0.85 }}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2} />{count != null && <span className="tabular-nums">{count}</span>}
    </button>
  );
}

// One post — OP or a reply. Left author panel + content + reaction row (Like/Dislike/Report/Reply).
export function ForumPost({
  P, post, targetType, targetId, isOP = false, nested = false,
  canDelete = false, onDelete, onReact, onReport, onReply, reacting = false, currentUser,
}) {
  return (
    <div className="border" style={{ borderColor: P.cardBorder, background: P.rowSurface }}
      data-testid={isOP ? "forum-thread-op" : `forum-reply-${targetId}`}>
      {nested && post.reply_to_username && (
        <div className="flex items-center gap-1.5 px-4 pt-2.5 font-mono text-[10px]" style={{ color: P.textMuted }} data-testid={`forum-reply-context-${targetId}`}>
          <CornerDownRight className="w-3 h-3" /> replying to <span style={{ color: P.lime }}>@{post.reply_to_username}</span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4">
        <AuthorPanel post={post} P={P} />
        <div className="min-w-0 flex-1" style={{ color: P.text }}>
          {isOP && (
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {post.pinned && <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[1.5px] px-2 py-0.5 border" style={{ color: P.goldText, background: P.gold, borderColor: P.gold }} data-testid="forum-op-pinned">Pinned</span>}
              {post.locked && <span className="font-mono text-[10px] uppercase tracking-[1.5px] px-2 py-0.5 border" style={{ color: P.textMuted, borderColor: P.cardBorder }} data-testid="forum-op-locked">Locked</span>}
              <span className="font-mono text-[10px] uppercase tracking-[1.5px] px-2 py-0.5 border" style={{ color: P.tagLimeText, background: P.tagLimeBg, borderColor: P.tagLimeBorder }}>{post.category_label}</span>
            </div>
          )}
          {isOP && <h1 className="font-heading font-black text-2xl sm:text-3xl leading-tight mb-2" style={{ color: P.text }} data-testid="forum-thread-heading">{post.title}</h1>}
          <p className="font-mono text-[10px] mb-3" style={{ color: P.textMuted }}>{new Date(post.created_at).toLocaleString()}</p>
          <div className="font-body text-[15px] whitespace-pre-wrap leading-relaxed" style={{ color: P.text }} data-testid={isOP ? "forum-thread-body" : `forum-reply-body-${targetId}`}>
            <Linkify text={post.body} linkStyle={{ color: P.lime }} />
          </div>

          {Array.isArray(post.images) && post.images.length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`forum-post-images-${targetId}`}>
              {post.images.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer" className="block border overflow-hidden" style={{ borderColor: P.cardBorder }}>
                  <img src={url} alt="" loading="lazy" data-testid={`forum-post-image-${targetId}-${i}`}
                    className="w-full h-28 object-cover hover:opacity-90 transition-opacity" />
                </a>
              ))}
            </div>
          )}
          {post.video && (
            <div className="mt-3" data-testid={`forum-post-video-${targetId}`}>
              <video src={post.video} controls playsInline preload="metadata"
                className="w-full max-w-md border bg-black" style={{ borderColor: P.cardBorder }} />
            </div>
          )}

          {/* Reaction row */}
          <div className="mt-4 flex items-center flex-wrap gap-2" style={{ color: P.textMuted }} data-testid={`forum-reactions-${targetId}`}>
            <ReactBtn icon={ThumbsUp} count={post.likes || 0} active={post.my_reaction === "like"} activeColor="#4e8a1f"
              onClick={() => onReact("like")} disabled={reacting} testid={`forum-react-like-${targetId}`} title="Like" />
            <ReactBtn icon={ThumbsDown} count={post.dislikes || 0} active={post.my_reaction === "dislike"} activeColor="#a33d28"
              onClick={() => onReact("dislike")} disabled={reacting} testid={`forum-react-dislike-${targetId}`} title="Dislike" />
            <ReactBtn icon={Flag} count={null} active={false} onClick={onReport} testid={`forum-report-${targetId}`} title="Report" />
            <ReactBtn icon={ReplyIcon} count={null} active={false} onClick={onReply} testid={`forum-reply-btn-${targetId}`} title="Reply" />
            {canDelete && (
              <button onClick={onDelete} data-testid={isOP ? "forum-thread-delete" : `forum-reply-delete-${targetId}`} title="Delete"
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide px-2 py-1.5 border ml-auto"
                style={{ color: "#e0736a", borderColor: "#e0736a55" }}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline reply composer used under the OP and under any reply.
export function ReplyBox({ P, value, onChange, onSubmit, onCancel, busy, disabled, placeholder, testid,
  images, setImages, video, setVideo }) {
  const canAttach = !disabled && typeof setImages === "function" && typeof setVideo === "function";
  return (
    <div className="border p-3" style={{ borderColor: P.cardBorder, background: P.rowSurface }} data-testid={testid}>
      <MentionTextarea P={P} value={value} onChange={onChange} rows={3} maxLength={8000} disabled={disabled}
        placeholder={placeholder} testid={`${testid}-input`}
        className="w-full bg-black/40 border px-3 py-2 text-sm resize-y outline-none disabled:opacity-60" style={{ color: P.text, borderColor: P.cardBorder }} />
      {canAttach && (
        <div className="mt-2">
          <ForumMediaUploader P={P} images={images || []} setImages={setImages} video={video || null} setVideo={setVideo} compact />
        </div>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button onClick={onSubmit} disabled={busy || disabled || !value.trim()} data-testid={`${testid}-submit`}
          className="inline-flex items-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2 bg-olive hover:bg-olive-bright text-white transition-colors disabled:opacity-50">
          <ReplyIcon className="w-4 h-4" /> Post reply
        </button>
        {onCancel && (
          <button onClick={onCancel} data-testid={`${testid}-cancel`} className="font-mono uppercase text-[11px] tracking-wide px-3 py-2 border" style={{ color: P.textMuted, borderColor: P.cardBorder }}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export function ReportModal({ P, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason || busy) return;
    setBusy(true);
    try { await onSubmit(reason, note.trim()); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="forum-report-modal">
      <div className="w-full max-w-sm border" style={{ background: P.rowSurface, borderColor: P.cardBorder }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: P.cardBorder }}>
          <h3 className="font-heading uppercase tracking-tight flex items-center gap-2" style={{ color: P.text }}><Flag className="w-4 h-4" style={{ color: P.lime }} /> Report</h3>
          <button onClick={onClose} data-testid="forum-report-close" style={{ color: P.textMuted }}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="font-mono text-[11px]" style={{ color: P.textMuted }}>A moderator will review this. No sales posts (those belong in the Marketplace) and no hate speech.</p>
          <div className="flex flex-col gap-1.5">
            {FORUM_REPORT_REASONS.map((r) => (
              <button key={r.value} onClick={() => setReason(r.value)} data-testid={`forum-report-reason-${r.value}`}
                className="text-left font-mono text-xs px-3 py-2 border transition-colors"
                style={reason === r.value ? { color: "#fff", background: P.lime, borderColor: P.lime, filter: "brightness(0.9)" } : { color: P.text, borderColor: P.cardBorder }}>
                {r.label}
              </button>
            ))}
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="Add a note (optional)" data-testid="forum-report-note"
            className="w-full bg-black/40 border px-3 py-2 text-sm resize-y outline-none" style={{ color: P.text, borderColor: P.cardBorder }} />
          <button onClick={submit} disabled={!reason || busy} data-testid="forum-report-submit"
            className="w-full inline-flex items-center justify-center gap-2 font-mono uppercase text-xs font-bold tracking-wide px-4 py-2.5 bg-olive hover:bg-olive-bright text-white transition-colors disabled:opacity-50">
            <Flag className="w-4 h-4" /> Submit report
          </button>
        </div>
      </div>
    </div>
  );
}
